/**
 * @file useBlockInserts.ts
 * @description Новые блоки на странице (phase19.16 Р2; контракт §16.2;
 * uiux-design-r2 §5, §6): секция из заготовки (`from-preset`), пустой блок
 * на 1–4 колонки, копия блока сразу ниже. Новый блок встаёт после
 * выбранного (иначе в конец; `null` — в начало страницы: сервер ставит
 * в конец, кабинет переставляет первым), запись — в «Отменить» с местом,
 * куда блок встал на самом деле, всплывашка «Добавлен блок «…»» с
 * [Отменить]. Лимит 30 блоков держит сервер (409 `page_blocks_limit`).
 */

import { defaultSectionPayload, type SectionPayload, type SectionPreset } from "@chaiberi/shared";
import { useCallback } from "react";

import type { UndoApi } from "./useUndoStack";
import { blockTitle } from "../../components/site-pages/block-meta";
import { sitePages, type SitePageBlock, type SitePageDetail } from "../../lib/api-site-pages";
import { addedEntry, snapshotOf, type HistoryEnv } from "../../lib/site-builder/block-undo";
import { sharesForColumns } from "../../lib/site-builder/section-edit";

/** Место вставки: id блока выше, `null` — в начало, `undefined` — в конец. */
export type Place = string | null | undefined;

interface Deps {
  page: SitePageDetail | null;
  /** Окружение истории открытой страницы; `null` — страницы нет. */
  env: HistoryEnv | null;
  refresh: () => Promise<void>;
  handleError: (err: unknown, fallback: string, blockId?: string) => void;
  toastUndo: (text: string) => void;
  undo: UndoApi;
}

export interface BlockInserts {
  /** Вставить заготовку; id нового блока или `null` (ошибка показана). */
  addPreset: (preset: SectionPreset, after: Place) => Promise<string | null>;
  /** Пустой блок на `columns` колонок. */
  addEmpty: (columns: number, after: Place) => Promise<string | null>;
  duplicate: (block: SitePageBlock) => Promise<string | null>;
}

/** Пустая секция на N колонок. */
export function emptySection(columns: number): SectionPayload {
  const base = defaultSectionPayload();
  const n = Math.min(4, Math.max(1, columns));
  return {
    ...base,
    columns: { ...base.columns, shares: sharesForColumns(n), items: Array.from({ length: n }, () => ({ elements: [] })) },
  };
}

/** Блок выше нового: в конец — последний блок страницы, в начало — `null`. */
export function anchorOf(after: Place, blocks: readonly SitePageBlock[]): string | null {
  if (after === undefined) return blocks[blocks.length - 1]?.id ?? null;
  return after;
}

export function useBlockInserts({ page, env, refresh, handleError, toastUndo, undo }: Deps): BlockInserts {
  const pageId = page?.id ?? null;

  /** Общий шаг: на место, перечитать, записать в историю, сказать человеку. */
  const finish = useCallback(
    async (inserted: SitePageBlock, label: string, after: Place) => {
      let created = inserted;
      const blocks = page?.blocks ?? [];
      // «В начало страницы»: вставленный в конец блок переставляем первым.
      if (after === null && pageId && blocks.length > 0) {
        const fresh = await sitePages.reorderBlocks(pageId, [created.id, ...blocks.map((b) => b.id).filter((id) => id !== created.id)]);
        // Перестановка переписала блоки — их новый updatedAt в ячейки (иначе ложный 409).
        env?.cells.sync(fresh);
        created = fresh.find((b) => b.id === created.id) ?? created;
      }
      const afterId = anchorOf(after, blocks);
      await refresh();
      const snap = snapshotOf(created);
      if (env && snap) undo.push(addedEntry(env, label, env.cells.refFor(created.id, created.updatedAt), snap, afterId));
      toastUndo(label);
      return created.id;
    },
    [env, page, pageId, refresh, toastUndo, undo],
  );

  const addPreset = useCallback(
    async (preset: SectionPreset, after: Place) => {
      if (!pageId) return null;
      try {
        const created = await sitePages.addFromPreset(pageId, preset.key, after);
        return await finish(created, `Добавлен блок «${preset.title}»`, after);
      } catch (err) {
        handleError(err, "Не удалось добавить блок.");
        return null;
      }
    },
    [finish, handleError, pageId],
  );

  const addEmpty = useCallback(
    async (columns: number, after: Place) => {
      if (!pageId) return null;
      try {
        const created = await sitePages.addSection(pageId, { payload: emptySection(columns), afterBlockId: after });
        return await finish(created, "Добавлен пустой блок", after);
      } catch (err) {
        handleError(err, "Не удалось добавить блок.");
        return null;
      }
    },
    [finish, handleError, pageId],
  );

  const duplicate = useCallback(
    async (block: SitePageBlock) => {
      if (!pageId) return null;
      try {
        const created = await sitePages.duplicateBlock(block.id);
        return await finish(created, `Скопирован блок «${blockTitle(block)}»`, block.id);
      } catch (err) {
        handleError(err, "Не удалось скопировать блок.", block.id);
        return null;
      }
    },
    [finish, handleError, pageId],
  );

  return { addPreset, addEmpty, duplicate };
}
